import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Send, Pause, Play } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Progress } from "@/components/ui/progress";

interface Story {
  id: string;
  user_id: string;
  media_type: 'image' | 'video';
  media_url: string;
  caption: string | null;
  duration_seconds: number;
  views_count: number;
  created_at: string;
  user: {
    name: string;
    avatar_url: string | null;
  };
}

interface StoryViewerProps {
  stories: Story[];
  onClose: () => void;
}

export const StoryViewer = ({ stories, onClose }: StoryViewerProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [message, setMessage] = useState("");
  const progressInterval = useRef<NodeJS.Timeout | null>(null);

  const currentStory = stories[currentIndex];
  const duration = currentStory.duration_seconds * 1000;

  // Marcar story como vista
  useEffect(() => {
    const markAsViewed = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Use upsert to avoid conflict errors when story was already viewed
      await supabase
        .from('story_views')
        .upsert(
          {
            story_id: currentStory.id,
            viewer_id: user.id
          },
          {
            onConflict: 'story_id,viewer_id',
            ignoreDuplicates: true
          }
        );
    };

    markAsViewed();
  }, [currentStory.id]);

  // Progress bar
  useEffect(() => {
    if (isPaused) {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
      return;
    }

    setProgress(0);
    const increment = 100 / (duration / 50);

    progressInterval.current = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          handleNext();
          return 0;
        }
        return prev + increment;
      });
    }, 50);

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [currentIndex, isPaused, duration]);

  const handleNext = () => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setProgress(0);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setProgress(0);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase
      .from('messages')
      .insert({
        sender_id: user.id,
        receiver_id: currentStory.user_id,
        content: message,
        message_type: 'text'
      });

    setMessage("");
    onClose();
  };

  return (
    <div className="relative w-full h-full bg-black flex items-center justify-center">
      {/* Progress Bars */}
      <div className="absolute top-4 inset-x-4 flex gap-1 z-20">
        {stories.map((_, index) => (
          <div key={index} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-white transition-all duration-100"
              style={{
                width: index < currentIndex ? '100%' : index === currentIndex ? `${progress}%` : '0%'
              }}
            />
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="absolute top-8 inset-x-4 flex items-center justify-between z-20">
        <div className="flex items-center gap-3">
          <Avatar className="w-10 h-10 border-2 border-white">
            <AvatarImage src={currentStory.user.avatar_url || undefined} />
            <AvatarFallback>{currentStory.user.name?.[0]}</AvatarFallback>
          </Avatar>
          <div>
            <p className="text-white font-semibold text-sm">{currentStory.user.name}</p>
            <p className="text-white/80 text-xs">
              {formatDistanceToNow(new Date(currentStory.created_at), {
                locale: ptBR,
                addSuffix: true
              })}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            size="icon"
            variant="ghost"
            className="text-white"
            onClick={() => setIsPaused(!isPaused)}
          >
            {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="text-white"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Media */}
      <div className="relative w-full h-full">
        {currentStory.media_type === 'image' ? (
          <img
            src={currentStory.media_url}
            alt="Story"
            className="w-full h-full object-contain"
          />
        ) : (
          <video
            src={currentStory.media_url}
            className="w-full h-full object-contain"
            autoPlay
            muted
            playsInline
          />
        )}

        {/* Caption */}
        {currentStory.caption && (
          <div className="absolute bottom-24 inset-x-4 bg-black/60 backdrop-blur-md p-4 rounded-lg border border-white/20">
            <p className="text-white text-sm whitespace-pre-line">{currentStory.caption}</p>
          </div>
        )}
      </div>

      {/* Tap areas */}
      <button
        className="absolute left-0 top-0 bottom-0 w-1/3 z-10"
        onClick={handlePrevious}
      />
      <button
        className="absolute right-0 top-0 bottom-0 w-1/3 z-10"
        onClick={handleNext}
      />

      {/* Reply Input */}
      <div className="absolute bottom-6 inset-x-4 z-20">
        <div className="flex gap-2">
          <Input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Responder..."
            className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
          />
          <Button
            size="icon"
            onClick={handleSendMessage}
            disabled={!message.trim()}
            className="bg-primary"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};